import i18next from 'i18next';
import { API_URL } from '@/lib/config';

// --- wire types (mirror services/api openapi.yaml) ---

export interface AuthTokens {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

export interface ApiUser {
  id: string;
  email?: string;
  phone?: string;
  role: string;
  is_minor: boolean;
  created_at: string;
}

export interface Profile {
  display_name: string;
  username?: string;
  favorite_player_id?: string;
  language: string;
  avatar_url?: string;
}

export interface Me {
  user: ApiUser;
  profile: Profile;
  email_verified?: boolean;
}

/** Typed error carrying the backend's `{ error: { code, message } }` envelope. */
export class ApiError extends Error {
  code: string;
  status: number;
  details?: Record<string, unknown>;
  /** The language `message` is written in (the response's Content-Language):
   *  the one the app asked for when the server has wording for `code`, else en. */
  lang?: string;

  constructor(
    message: string,
    code: string,
    status: number,
    details?: Record<string, unknown>,
    lang?: string,
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
    this.lang = lang;
  }
}

async function handle<T>(res: Response): Promise<T> {
  if (res.status === 204) {
    return undefined as T;
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = (data as any)?.error ?? {};
    throw new ApiError(
      err.message ?? 'Something went wrong.',
      err.code ?? 'error',
      res.status,
      err.details,
      res.headers.get('content-language') ?? undefined,
    );
  }
  return data as T;
}

/** The UI language, so the server writes error messages in it. Imported from
 *  i18next itself, not '@/i18n', which would close an import cycle
 *  (i18n -> queryClient -> auth). */
function langHeader(): Record<string, string> {
  return i18next.language ? { 'Accept-Language': i18next.language } : {};
}

function post<T>(path: string, body: unknown, token?: string): Promise<T> {
  return fetch(`${API_URL}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...langHeader(),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  }).then((res) => handle<T>(res));
}

function get<T>(path: string, token: string): Promise<T> {
  return fetch(`${API_URL}${path}`, {
    headers: { Authorization: `Bearer ${token}`, ...langHeader() },
  }).then((res) => handle<T>(res));
}

function del<T>(path: string, token: string): Promise<T> {
  return fetch(`${API_URL}${path}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}`, ...langHeader() },
  }).then((res) => handle<T>(res));
}

// --- endpoints ---

export function register(email: string, password: string, displayName: string): Promise<AuthTokens> {
  return post<AuthTokens>('/auth/register', { email, password, display_name: displayName });
}

/** The password rules the server enforces. Public — needed before sign-up. */
export function getPasswordPolicy(): Promise<unknown> {
  return fetch(`${API_URL}/auth/password-policy`, { headers: { ...langHeader() } }).then((res) =>
    handle<unknown>(res),
  );
}

export function login(email: string, password: string): Promise<AuthTokens> {
  return post<AuthTokens>('/auth/login', { email, password });
}

export function googleAuth(idToken: string): Promise<AuthTokens> {
  return post<AuthTokens>('/auth/oauth/google', { id_token: idToken });
}

export function appleAuth(idToken: string): Promise<AuthTokens> {
  return post<AuthTokens>('/auth/oauth/apple', { id_token: idToken });
}

export function refreshTokens(refreshToken: string): Promise<AuthTokens> {
  return post<AuthTokens>('/auth/refresh', { refresh_token: refreshToken });
}

export function logout(refreshToken: string): Promise<void> {
  return post<void>('/auth/logout', { refresh_token: refreshToken });
}

export function getMe(accessToken: string): Promise<Me> {
  return fetch(`${API_URL}/me`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  }).then((res) => handle<Me>(res));
}

export interface AgeGateResult {
  is_minor: boolean;
  requires_parent_approval: boolean;
}

/** Records minor status server-side from a birthdate (never stores the DOB itself). */
export function ageGate(accessToken: string, birthdate: string): Promise<AgeGateResult> {
  return post<AgeGateResult>('/me/age-gate', { birthdate }, accessToken);
}

export interface ParentalConsentResult {
  status: 'pending' | 'approved' | 'declined';
  parent_email: string;
}

/** Requests parental consent for a minor account — blocks purchases until approved. */
export function parentalConsent(accessToken: string, parentEmail: string): Promise<ParentalConsentResult> {
  return post<ParentalConsentResult>('/me/parental-consent', { parent_email: parentEmail }, accessToken);
}

export interface SessionInfo {
  id: string;
  device?: string;
  user_agent?: string;
  ip?: string;
  created_at: string;
  last_active_at: string;
  expires_at: string;
  /** True for the session this request was made from — "this device". */
  current: boolean;
}

/** Lists the caller's active login sessions (one per refresh token in use). */
export function listSessions(accessToken: string): Promise<SessionInfo[]> {
  return get<{ sessions: SessionInfo[] }>('/me/sessions', accessToken).then((r) => r.sessions ?? []);
}

/** Revokes one of the caller's sessions, signing that device out. */
export function revokeSession(accessToken: string, sessionId: string): Promise<void> {
  return del<void>(`/me/sessions/${sessionId}`, accessToken);
}

/** Sends a 6-digit SMS one-time code to an E.164 phone number. */
export function requestOtp(phone: string): Promise<void> {
  return post<void>('/auth/otp/request', { phone });
}

/** Verifies an SMS one-time code and signs the fan in, creating the account on first verify. */
export function verifyOtp(phone: string, code: string): Promise<AuthTokens> {
  return post<AuthTokens>('/auth/otp/verify', { phone, code });
}

// --- password reset and email verification (fan QA 2026-09-18, H8, M9) ---

/** Emails a password-reset code. Always 202, whether or not the address has an account. */
export function forgotPassword(email: string): Promise<void> {
  return post<void>('/auth/password/forgot', { email });
}

/** Sets a new password with the emailed code. Signs the account out everywhere. */
export function resetPassword(email: string, code: string, newPassword: string): Promise<void> {
  return post<void>('/auth/password/reset', { email, code, new_password: newPassword });
}

/** Emails a verification code to the address. */
export function requestEmailCode(email: string): Promise<void> {
  return post<void>('/auth/email/otp/request', { email });
}

/** Verifies the emailed code and marks the address verified. */
export function verifyEmailCode(email: string, code: string): Promise<void> {
  return post<void>('/auth/email/otp/verify', { email, code });
}
